
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  register,
  registerInvitedCreator,
  acceptRecipientInvite,
  checkAvailability,
  getRecipientInviteInfo,
  getUserProfile,
  setUserType
} from '../../services/api';
import { storePartnerCode } from '../../utils/partnerTracking';
import { track } from '../../services/measurement';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from './AuthContext';
import { 
  Eye, EyeOff, Mail, Lock, 
  ArrowRight, AlertCircle, Sparkles,
  ChevronLeft, CheckCircle2, Building2,
  ArrowLeft
} from 'lucide-react';
import '../auth/AuthForms.scss';

const Register = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, refreshUser } = useAuth();
  const googleWrapperRef = useRef(null);
  
  const queryParams = new URLSearchParams(location.search);
  const inviteToken = queryParams.get('invite_token');
  const partnerCode = queryParams.get('partner');
  const nextParam = queryParams.get('next');
  const hasInvite = Boolean(inviteToken);
  const inviteReturnTo = hasInvite ? sessionStorage.getItem('foth_invite_return_to') : null;
  const loginParams = new URLSearchParams();
  if (inviteToken) loginParams.set('invite_token', inviteToken);
  if (partnerCode) loginParams.set('partner', partnerCode);
  if (nextParam) loginParams.set('next', nextParam);
  const loginQuery = loginParams.toString();
  const loginPath = loginQuery ? `/login?${loginQuery}` : '/login';

  // Multi-step state
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleSubmitting, setRoleSubmitting] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);
  const [registrationErrorAction, setRegistrationErrorAction] = useState(null);
  const [emailExists, setEmailExists] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [invitedCollab, setInvitedCollab] = useState(null);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isGoogleLogin, setIsGoogleLogin] = useState(false);
  const [googleButtonWidth, setGoogleButtonWidth] = useState('400');
  const [invalidInvite, setInvalidInvite] = useState(false);

  const resumePendingRegistration = (data) => {
    if (!data?.user_id || !data?.email) return false;

    setCreatedUserId(data.user_id);
    setRegisteredEmail(data.email);
    sessionStorage.setItem('foth_pending_registration', JSON.stringify({
      userId: data.user_id,
      email: data.email
    }));

    if (data.needs_role || data.user_type === 'none') {
      setStep(2);
      return true;
    }

    sessionStorage.setItem('foth_pending_verification', JSON.stringify({
      userId: data.user_id,
      email: data.email,
      role: data.role || data.user_type,
      inviteToken
    }));
    navigate('/verify-account', {
      state: {
        userId: data.user_id,
        email: data.email,
        role: data.role || data.user_type,
        inviteToken,
      }
    });
    return true;
  };

  useEffect(() => {
    if (hasInvite) return;

    try {
      const pendingRegistration = JSON.parse(sessionStorage.getItem('foth_pending_registration') || 'null');
      if (pendingRegistration?.userId && pendingRegistration?.email) {
        setCreatedUserId(pendingRegistration.userId);
        setRegisteredEmail(pendingRegistration.email);
        setStep(2);
      }
    } catch (err) {
      sessionStorage.removeItem('foth_pending_registration');
    }
  }, [hasInvite]);

  useEffect(() => {
    if (partnerCode) {
      storePartnerCode(partnerCode);
    }
  }, [partnerCode]);

  useEffect(() => {
    const updateGoogleButtonWidth = () => {
      if (googleWrapperRef.current) {
        const measured = googleWrapperRef.current.offsetWidth;
        if (measured > 150) {
          setGoogleButtonWidth(String(Math.min(400, Math.floor(measured))));
          return;
        }
      }
      const viewportWidth = window.innerWidth || 400;
      const deduction = viewportWidth <= 600 ? 72 : 80;
      const safeWidth = Math.min(400, Math.max(260, viewportWidth - deduction));
      setGoogleButtonWidth(String(Math.floor(safeWidth)));
    };

    updateGoogleButtonWidth();
    window.addEventListener('resize', updateGoogleButtonWidth);
    const tick = setTimeout(updateGoogleButtonWidth, 120);
    return () => {
      window.removeEventListener('resize', updateGoogleButtonWidth);
      clearTimeout(tick);
    };
  }, []);

  const RegistrationSchema = useMemo(() => Yup.object().shape({
    email: Yup.string()
      .email(t('errors.email_invalid'))
      .required(t('errors.email_required')),
    password: Yup.string()
      .min(8, t('errors.password_min'))
      .matches(/[a-z]/, t('errors.password_min'))
      .matches(/[A-Z]/, t('errors.password_min'))
      .matches(/[0-9]/, t('errors.password_min'))
      .required(t('errors.password_required')),
    password2: Yup.string()
      .oneOf([Yup.ref('password'), null], t('errors.password_match'))
      .required(t('errors.password_confirm')),
  }), [t]);

  useEffect(() => {
    const fetchInviteInfo = async () => {
      if (inviteToken) {
        try {
          const res = await getRecipientInviteInfo(inviteToken);
          setInvitedCollab(res.data);
          setInvalidInvite(false);
          if (res.data?.invited_email) {
            try {
              const availability = await checkAvailability({ email: res.data.invited_email });
              setEmailExists(Boolean(availability.data.email_exists));
            } catch (availabilityErr) {
              setEmailExists(false);
            }
          }
        } catch (err) {
          setInvalidInvite(true);
        }
      }
      setIsLoading(false);
    };
    fetchInviteInfo();
  }, [inviteToken]);

  const handleRoleSelection = async (role) => {
    if (!role || roleSubmitting) return;
    setRoleSubmitting(true);
    try {
      setRegistrationError(null);
      setRegistrationErrorAction(null);
      track('onboarding_started', { user_role: role });
      
      // CRITICAL: Refresh user state if already logged in (Google Login)
      // to update user_type from "none" to the selected role
      if (isGoogleLogin) {
        await setUserType(role);
        await refreshUser();
        if (inviteToken && role === 'creator') {
          const accepted = await acceptRecipientInvite(inviteToken);
          await refreshUser();
          const targetPath = `/creator/invited/${accepted.data.offer_id}`;
          sessionStorage.removeItem('foth_invite_return_to');
          navigate(targetPath, { replace: true });
          return;
        }
        // If Google login, user is already verified and logged in
        const target = partnerCode && role === 'business'
          ? (nextParam || '/business/payment/plans')
          : '/complete-profile';
        navigate(inviteReturnTo ? `/complete-profile?returnTo=${encodeURIComponent(inviteReturnTo)}` : target);
      } else {
        sessionStorage.setItem('foth_pending_verification', JSON.stringify({
          userId: createdUserId,
          email: registeredEmail,
          role,
          inviteToken
        }));
        // Navigate to /verify-account (correct path) and pass role for final redirect
        navigate('/verify-account', {
          state: { email: registeredEmail, userId: createdUserId, role, inviteToken }
        });
      }
    } catch (err) {
      setRegistrationError(t('role_selection.error'));
    } finally {
      setRoleSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setRegistrationError(null);
      setRegistrationErrorAction(null);
      const res = await loginWithGoogle(credentialResponse.credential, true);
      if (res.user.user_type === 'none') {
        setCreatedUserId(res.user.id);
        setRegisteredEmail(res.user.email);
        setIsGoogleLogin(true);
        setStep(2);
      } else if (inviteToken && res.user.user_type === 'creator') {
        const accepted = await acceptRecipientInvite(inviteToken);
        await refreshUser();
        const targetPath = `/creator/invited/${accepted.data.offer_id}`;
        sessionStorage.removeItem('foth_invite_return_to');
        navigate(targetPath, { replace: true });
      } else {
        navigate(res.user.is_profile_completed ? '/dashboard' : '/complete-profile');
      }
    } catch (err) {
      const data = err.response?.data || {};
      setRegistrationError(data.error || t('errors.google_error'));
      setRegistrationErrorAction(data.account_exists || data.code === 'account_exists'
        ? { to: loginPath, label: t('register.errors.login_link') }
        : null);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-loading">
        <motion.div 
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="loading-logo"
        >
          FOTH
        </motion.div>
      </div>
    );
  }

  if (invalidInvite) {
    return (
      <div className="editorial-split">
        <div className="auth-editorial-side">
          <div className="editorial-bg-overlay" />
          <div className="editorial-content">
            <Link to="/" className="auth-logo-inverse">FOTH</Link>
          </div>
        </div>
        <div className="auth-form-side">
          <div className="auth-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="auth-card-modern"
            >
              <div className="alert-premium error">
                <AlertCircle size={18} /> {t('errors.invite_invalid')}
              </div>
              <Link to="/" className="auth-submit-btn-premium">
                {t('common:buttons.back')}
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="editorial-split">
      {/* ── LEFT: EDITORIAL SIDE ── */}
      <div className="auth-editorial-side">
        <div className="editorial-bg-overlay" />
        <div className="editorial-content">
          <Link to="/" className="auth-logo-inverse">FOTH</Link>
          <div className="editorial-text">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {t('register.editorial.title')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {t('register.editorial.text')}
            </motion.p>
          </div>
          <div className="editorial-footer" />
        </div>
      </div>

      {/* ── RIGHT: FORM SIDE ── */}
      <div className="auth-form-side">
        <div className="auth-container">
          {/* Persistent Mobile Header */}
          <div className="auth-mobile-header">
            {step === 1 ? (
              <Link to="/" className="mobile-back-btn" aria-label={t('common:back', 'Volver')}>
                <ArrowLeft size={18} />
              </Link>
            ) : (
              <button className="mobile-back-btn" onClick={() => setStep(1)} aria-label={t('common:back', 'Volver')}>
                <ChevronLeft size={18} />
              </button>
            )}
            <div className="mobile-brand-box">
              <img src="/foth-luxury-icon.png" alt="FOTH" className="mobile-logo" />
            </div>
            <div className="spacer" />
          </div>

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="auth-card-modern"
              >
                <div className="auth-header">
                  
                  {invitedCollab && (
                    <div className="invite-badge-box">
                      <Sparkles size={16} />
                      <div>
                        <strong>{t('register.invite.title', { business_name: invitedCollab.business_name })}</strong>
                        <p>{t('register.invite.subtitle', { title: invitedCollab.title })}</p>
                      </div>
                    </div>
                  )}

                  <h2 className="auth-title">{t('register.title')}</h2>
                  <p className="auth-subtitle">{t('register.subtitle')}</p>
                </div>

                {!hasInvite && (
                  <>
                    <div className="google-auth-wrapper" ref={googleWrapperRef}>
                      <div className="google-auth-provider">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={() => setRegistrationError(t('errors.google_error'))}
                          theme="filled_black"
                          shape="pill"
                          text="signup_with"
                          width={googleButtonWidth}
                        />
                      </div>
                    </div>

                    <div className="auth-divider">
                      <span>{t('register.google', 'O regístrate con tu correo')}</span>
                    </div>
                  </>
                )}

                <Formik
                  enableReinitialize
                  initialValues={{ email: invitedCollab?.invited_email || '', password: '', password2: '' }}
                  validationSchema={RegistrationSchema}
                  onSubmit={async (values, { setSubmitting }) => {
                    try {
                      setRegistrationError(null);
                      setRegistrationErrorAction(null);

                      if (hasInvite) {
                        const payload = {
                          ...values,
                          invite_token: inviteToken,
                          channel: sessionStorage.getItem('foth_invite_channel') || 'general'
                        };
                        const res = await registerInvitedCreator(payload);
                        sessionStorage.removeItem('foth_invite_token');
                        track('sign_up', { method: 'password', user_role: 'creator', invited: true });

                        let userData = {
                          id: res.data.user_id,
                          username: res.data.username,
                          user_type: 'creator',
                          is_active: true,
                          is_profile_completed: false,
                          application_submitted: false,
                          creator_profile: {
                            is_invited: true,
                            invited_collab: res.data.invite_collab_id,
                            invited_offer_ids: [res.data.invite_collab_id],
                            has_invited_access: true
                          }
                        };

                        try {
                          const profileRes = await getUserProfile();
                          userData = profileRes.data;
                        } catch (profileErr) {
                          // The token/user fallback is enough for route gating; AuthContext will refresh later.
                        }

                        await login(null, userData);
                        const targetPath = `/creator/invited/${res.data.invite_collab_id}`;
                        sessionStorage.removeItem('foth_invite_return_to');
                        navigate(targetPath, { replace: true });
                        return;
                      }

                      const payload = { ...values };
                      
                      const res = await register(payload);
                      track('sign_up', { method: 'password' });
                      // Backend returns user_id, not id
                      const userId = res.data.user_id || res.data.id;
                      setCreatedUserId(userId);
                      setRegisteredEmail(values.email);
                      sessionStorage.setItem('foth_pending_registration', JSON.stringify({
                        userId,
                        email: values.email
                      }));
                      setStep(2);
                    } catch (err) {
                      const data = err.response?.data || {};
                      if (data.code === 'account_pending_verification' && resumePendingRegistration(data)) {
                        return;
                      }
                      setRegistrationError(data.email?.[0] || data.error || t('errors.generic_error'));
                      setRegistrationErrorAction(data.code === 'account_exists'
                        ? { to: loginPath, label: t('register.errors.login_link') }
                        : null);
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ isSubmitting, values, setFieldValue }) => (
                    <Form className="auth-form">
                      {registrationError && (
                        <div className="alert-premium error">
                          <AlertCircle size={18} /> {registrationError}
                          {registrationErrorAction && (
                            <>
                              {' '}
                              <Link to={registrationErrorAction.to}>{registrationErrorAction.label}</Link>
                            </>
                          )}
                        </div>
                      )}

                      <div className="form-group-full">
                        <label className="form-label">{t('register.form.email', 'Correo electrónico')}</label>
                        <div className="input-premium-wrapper">
                          <Mail className="input-icon" size={18} />
                          <Field
                            name="email"
                            type="email"
                            className="form-control-premium"
                            placeholder={t('register.form.placeholder.email', 'tu@email.com')}
                            disabled={hasInvite && Boolean(invitedCollab?.invited_email)}
                            onChange={(e) => {
                              setEmailExists(false);
                              setRegistrationError(null);
                              setRegistrationErrorAction(null);
                              setFieldValue('email', e.target.value);
                            }}
                            onBlur={async (e) => {
                              const email = e.target.value.trim();
                              if (email) {
                                try {
                                  const res = await checkAvailability({ email });
                                  setEmailExists(Boolean(res.data.email_exists));
                                } catch (err) {
                                  setEmailExists(false);
                                }
                              }
                            }}
                          />
                        </div>
                        <ErrorMessage name="email" component="div" className="error-message" />
                        {emailExists && (
                          <div className="error-message">
                            {hasInvite ? (
                              <>
                                {t('errors.invite_email_exists')}{' '}
                                <Link to={loginPath}>{t('login.footer.login_link')}</Link>
                              </>
                            ) : (
                              <>
                                {t('errors.email_exists')}{' '}
                                <Link to={loginPath}>{t('register.errors.login_link')}</Link>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="form-group-full">
                        <label className="form-label">{t('register.form.password', 'Contraseña')}</label>
                        <div className="input-premium-wrapper">
                          <Lock className="input-icon" size={18} />
                          <Field
                            name="password"
                            type={showPassword ? "text" : "password"}
                            className="form-control-premium"
                            placeholder={t('register.form.placeholder.password', 'Mín. 8 caracteres')}
                          />
                          <button 
                            type="button" 
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        <PasswordStrengthMeter password={values.password} />
                        <ErrorMessage name="password" component="div" className="error-message" />
                      </div>

                      <div className="form-group-full">
                        <label className="form-label">{t('register.form.confirm', 'Confirmar contraseña')}</label>
                        <div className="input-premium-wrapper">
                          <Lock className="input-icon" size={18} />
                          <Field
                            name="password2"
                            type="password"
                            className="form-control-premium"
                            placeholder={t('register.form.placeholder.confirm', 'Repite contraseña')}
                          />
                        </div>
                        <ErrorMessage name="password2" component="div" className="error-message" />
                      </div>

                      <button 
                        type="submit" 
                        className="auth-submit-btn-premium" 
                        disabled={isSubmitting || emailExists}
                      >
                        {isSubmitting ? <div className="btn-spinner" /> : (
                          <>
                            {t('register.submit', 'Aplicar')} <ArrowRight size={18} />
                          </>
                        )}
                      </button>
                    </Form>
                  )}
                </Formik>

                <div className="auth-footer-minimal">
                  <p>
                    {t('register.footer.has_account')}{' '}
                    <Link to={loginPath}>{t('register.footer.login_link')}</Link>
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="auth-card-modern"
              >
                {/* Standard Back Link for Desktop */}
                <button className="back-link desktop-only" onClick={() => setStep(1)}>
                  <ChevronLeft size={16} /> {t('common:buttons.back')}
                </button>

                <div className="auth-header">
                  <h2 className="auth-title">{t('role_selection.title')}</h2>
                  <p className="auth-subtitle">{t('role_selection.subtitle')}</p>
                </div>

                <div className="roles-selection-integrated">
                  <button
                    type="button"
                    className={`role-card-mini creator ${selectedRole === 'creator' ? 'active' : ''}`}
                    aria-pressed={selectedRole === 'creator'}
                    disabled={roleSubmitting}
                    onClick={() => setSelectedRole('creator')}
                  >
                    <div className="role-icon-wrap">
                      <Sparkles size={24} />
                    </div>
                    <div className="role-info">
                      <h4>{t('role_selection.creator.title')}</h4>
                      <p>{t('role_selection.creator.description')}</p>
                    </div>
                    <CheckCircle2 className="check" size={20} />
                  </button>

                  <button
                    type="button"
                    className={`role-card-mini business ${selectedRole === 'business' ? 'active' : ''}`}
                    aria-pressed={selectedRole === 'business'}
                    disabled={roleSubmitting}
                    onClick={() => setSelectedRole('business')}
                  >
                    <div className="role-icon-wrap">
                      <Building2 size={24} />
                    </div>
                    <div className="role-info">
                      <h4>{t('role_selection.business.title')}</h4>
                      <p>{t('role_selection.business.description')}</p>
                    </div>
                    <CheckCircle2 className="check" size={20} />
                  </button>
                </div>

                <p className="auth-subtitle">{t('role_selection.change_note')}</p>
                {!selectedRole && <p className="auth-subtitle">{t('role_selection.select_hint')}</p>}
                <button
                  type="button"
                  className="auth-submit-btn-premium"
                  disabled={!selectedRole || roleSubmitting}
                  onClick={() => handleRoleSelection(selectedRole)}
                >
                  {roleSubmitting ? <div className="btn-spinner" /> : selectedRole ? t(`role_selection.continue_${selectedRole}`) : t('role_selection.submit')}
                </button>

                {registrationError && (
                  <div className="alert-premium error">
                    <AlertCircle size={18} /> {registrationError}
                    {registrationErrorAction && (
                      <>
                        {' '}
                        <Link to={registrationErrorAction.to}>{registrationErrorAction.label}</Link>
                      </>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Register;
